import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  AprendizajeEsperado,
  CriterioEvaluacion,
  OASelectionV2,
  ObjetivoAprendizajeV2,
  ObjetivoGenerico,
  OATreeSelection,
  AENode,
  CENode,
  OGNode,
} from "./types";

// Estructura seleccionada para renderizar en la columna derecha y construir el arbol.
export type SelectedTree = Record<
  string,
  {
    id: string;
    texto: string;
    criterios: Record<
      string,
      {
        id: string;
        texto: string;
        objetivos: Record<string, { id: string; texto: string }>;
      }
    >;
  }
>;

const fromTreeToSelected = (tree?: OATreeSelection | null): SelectedTree => {
  if (!tree?.aprendizajesEsperados?.length) return {};
  const acc: SelectedTree = {};
  tree.aprendizajesEsperados.forEach((ae) => {
    acc[ae.id] = { id: ae.id, texto: ae.texto, criterios: {} };
    ae.criteriosEvaluacion.forEach((ce) => {
      acc[ae.id].criterios[ce.id] = { id: ce.id, texto: ce.texto, objetivos: {} };
      ce.objetivosGenericos.forEach((og) => {
        acc[ae.id].criterios[ce.id].objetivos[og.id] = { id: og.id, texto: og.texto };
      });
    });
  });
  return acc;
};

interface Params {
  open: boolean;
  objetivo: ObjetivoAprendizajeV2;
  initialSelection?: OASelectionV2;

  aprendizajes: AprendizajeEsperado[];
  criteriosByAE: Record<string, CriterioEvaluacion[]>;
  objetivosByCriterio: Record<string, ObjetivoGenerico[]>;
}

export const useOATreeSelection = ({
  open,
  objetivo,
  initialSelection,
  aprendizajes,
  criteriosByAE,
  objetivosByCriterio,
}: Params) => {
  const [selected, setSelected] = useState<SelectedTree>({});

  // Map para resolver textos de los aprendizajes Esperados
  const aeMap = useMemo(() => {
    const map = new Map<string, AprendizajeEsperado>();
    aprendizajes.forEach((ae) => map.set(ae.id, ae));
    return map;
  }, [aprendizajes]);


  useEffect(() => {
    if (!open) return;
    const tree = initialSelection?.tree ?? null;
    setSelected(fromTreeToSelected(tree));
  }, [open, initialSelection]);

  const isOGSelected = useCallback(
    (aeId: string, ceId: string, ogId: string) => Boolean(selected[aeId]?.criterios?.[ceId]?.objetivos?.[ogId]),
    [selected]
  );

  const addOGSelection = useCallback(
    (aeId: string, ceId: string, ogId: string) => {
      const ae = aeMap.get(aeId);
      const ce = (criteriosByAE[aeId] ?? []).find((x) => x.id === ceId);
      const og = (objetivosByCriterio[ceId] ?? []).find((x) => x.id === ogId);
      if (!ae || !ce || !og) return;

      setSelected((prev) => {
        const copy: SelectedTree = { ...prev };

        if (!copy[aeId]) copy[aeId] = { id: ae.id, texto: ae.texto, criterios: {} };
        if (!copy[aeId].criterios[ceId]) copy[aeId].criterios[ceId] = { id: ce.id, texto: ce.texto, objetivos: {} };

        copy[aeId].criterios[ceId].objetivos[ogId] = { id: og.id, texto: og.texto };
        return copy;
      });
    },
    [aeMap, criteriosByAE, objetivosByCriterio]
  );

  const removeOGSelection = useCallback((aeId: string, ceId: string, ogId: string) => {
    setSelected((prev) => {
      const copy: SelectedTree = { ...prev };
      const aeNode = copy[aeId];
      if (!aeNode) return prev;

      const ceNode = aeNode.criterios[ceId];
      if (!ceNode) return prev;

      if (!ceNode.objetivos[ogId]) return prev;

      const objetivos = { ...ceNode.objetivos };
      delete objetivos[ogId];

      // si quedó sin OG, eliminar CE
      if (Object.keys(objetivos).length === 0) {
        const criterios = { ...aeNode.criterios };
        delete criterios[ceId];

        // si quedó sin Criterios de Evaluación, se elimina todo el Aprendizaje esperado
        if (Object.keys(criterios).length === 0) {
          delete copy[aeId];
          return copy;
        }

        copy[aeId] = { ...aeNode, criterios };
        return copy;
      }

      copy[aeId] = {
        ...aeNode,
        criterios: { ...aeNode.criterios, [ceId]: { ...ceNode, objetivos } },
      };
      return copy;
    });
  }, []);

  const toggleOGSelection = useCallback(
    (aeId: string, ceId: string, ogId: string) => {
      if (isOGSelected(aeId, ceId, ogId)) {
        removeOGSelection(aeId, ceId, ogId);
      } else {
        addOGSelection(aeId, ceId, ogId);
      }
    },
    [addOGSelection, isOGSelected, removeOGSelection]
  );

  const clearAll = useCallback(() => setSelected({}), []);

  const selectedCounts = useMemo(() => {
    const aeCount = Object.keys(selected).length;
    const ceSet = new Set<string>();
    const ogSet = new Set<string>();

    Object.values(selected).forEach((ae) => {
      Object.values(ae.criterios).forEach((ce) => {
        ceSet.add(ce.id);
        Object.values(ce.objetivos).forEach((og) => ogSet.add(og.id));
      });
    });

    return { aeCount, ceCount: ceSet.size, ogCount: ogSet.size };
  }, [selected]);

  const canConfirm = selectedCounts.ogCount > 0;

  const resumen = useMemo(() => {
    const aprendizajesTexto: string[] = [];
    const criteriosTextoSet = new Set<string>();
    const objetivosGenericosTexto: string[] = [];

    Object.values(selected).forEach((ae) => {
      aprendizajesTexto.push(ae.texto);
      Object.values(ae.criterios).forEach((ce) => {
        criteriosTextoSet.add(ce.texto);
        Object.values(ce.objetivos).forEach((og) => objetivosGenericosTexto.push(og.texto));
      });
    });

    return {
      aprendizajesTexto,
      criteriosTexto: Array.from(criteriosTextoSet.values()),
      objetivosGenericosTexto,
    };
  }, [selected]);

  const buildTreeSelection = useCallback((): OATreeSelection => {
    const aprendizajesEsperados: AENode[] = Object.values(selected).map((ae) => {
      const criteriosEvaluacion: CENode[] = Object.values(ae.criterios).map((ce) => {
        const objetivosGenericos: OGNode[] = Object.values(ce.objetivos).map((og) => ({
          id: og.id,
          texto: og.texto,
        }));

        return { id: ce.id, texto: ce.texto, objetivosGenericos };
      });

      return { id: ae.id, texto: ae.texto, criteriosEvaluacion };
    });

    return {
      objetivo: { id: objetivo.id, codigo: objetivo.codigo, texto: objetivo.objetivo },
      aprendizajesEsperados,
    };
  }, [objetivo, selected]);

  const buildSelection = useCallback((): OASelectionV2 => {
    const aprendizajeIds = Object.keys(selected);

    const criterioSet = new Set<string>();
    const ogSet = new Set<string>();

    Object.values(selected).forEach((ae) => {
      Object.values(ae.criterios).forEach((ce) => {
        criterioSet.add(ce.id);
        Object.values(ce.objetivos).forEach((og) => ogSet.add(og.id));
      });
    });

    return {
      objetivoId: objetivo.id,
      objetivoTexto: objetivo.objetivo,

      aprendizajeIds,
      criterioIds: Array.from(criterioSet),
      objetivoGenericoIds: Array.from(ogSet),

      aprendizajesTexto: resumen.aprendizajesTexto,
      criteriosTexto: resumen.criteriosTexto,
      objetivosGenericosTexto: resumen.objetivosGenericosTexto,

      tree: buildTreeSelection(),
    };
  }, [buildTreeSelection, objetivo.id, objetivo.objetivo, resumen, selected]);

  return {
    selected,
    setSelected,
    isOGSelected,
    toggleOGSelection,
    removeOGSelection,
    clearAll,
    selectedCounts,
    canConfirm,
    resumen,
    buildSelection,
  };
};
